import { getProfileName } from "PFCore/helpers/profile";
import { ActivityWarning } from "PFCore/hooks/queries/activity";
import { Id, Profile } from "PFTypes";
import { Option } from "PFTypes/options";
import React, { RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { WarningsDetail } from "../../warnings_detail/warnings_detail";
import { WarningsFilter } from "../../warnings_detail/warnings_filter";
import { MisalignmentsList } from "./misalignments_list";

const ALL_OPTION_ID = "all";
const NO_VACANCY_OPTION_ID = "no-vacancy"; // in case of potential misalignments for a non-existing vacancy
const getRelatedVacancyId = ({
  profileId,
  misalignments,
  defaultValue
}: {
  profileId?: Id;
  misalignments: ActivityWarning[];
  defaultValue: Id;
}): Id => {
  if (profileId) {
    const profileMisalignment = misalignments.find(({ profileIds }) =>
      profileIds.includes(profileId as number)
    );
    return profileMisalignment?.vacancyId ?? defaultValue;
  }
  return defaultValue;
};

type MisalignmentsDetailProps = {
  profileId: Id | undefined;
  profiles: Profile[];
  misalignments: ActivityWarning[];
  goToDate?: (date: Date) => void;
  sidePanelRef: RefObject<HTMLDivElement | null>;
};

export const MisalignmentsDetail = ({
  profileId,
  profiles,
  misalignments,
  goToDate,
  sidePanelRef
}: MisalignmentsDetailProps) => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "details.misalignments" });

  const isSingleVacancy = misalignments.length === 1;

  const allOption: Option<Id> = {
    id: ALL_OPTION_ID,
    value: t("allVacancies", { count: misalignments.length }),
    original: ALL_OPTION_ID
  };

  const workforceMemberOptions: Option<Id>[] = misalignments.map(({ profileIds, vacancyId }) => ({
    id: vacancyId ?? NO_VACANCY_OPTION_ID,
    value: profileIds
      .map((profileId) => getProfileName(profiles.find(({ id }) => id === profileId)))
      .join(", "),
    original: vacancyId ?? NO_VACANCY_OPTION_ID
  }));

  const options = isSingleVacancy ? workforceMemberOptions : [allOption, ...workforceMemberOptions];

  const vacancyId = getRelatedVacancyId({
    profileId,
    misalignments,
    defaultValue: options[0].id
  });
  const [selectedVacancy, setSelectedVacancy] = useState<Id>(vacancyId);

  useEffect(() => {
    setSelectedVacancy(vacancyId);
  }, [vacancyId]);

  const filteredMisalignments =
    selectedVacancy === ALL_OPTION_ID
      ? misalignments
      : misalignments.filter(({ vacancyId }) => {
          if (selectedVacancy === NO_VACANCY_OPTION_ID) {
            return vacancyId === null;
          } else {
            return vacancyId === selectedVacancy;
          }
        });

  const misalignmentsCount = filteredMisalignments.flatMap(({ misalignments }) => misalignments).length;

  return (
    <WarningsDetail
      customHeader={
        <WarningsFilter
          label={t("workforceMemberLabel")}
          onChangeSelectedOption={setSelectedVacancy}
          selectedOption={selectedVacancy}
          options={options}
          sidePanelRef={sidePanelRef}
        />
      }
      summary={t("counter", { count: misalignmentsCount })}
      sidePanelRef={sidePanelRef}
    >
      {(selectedOrder) => (
        <MisalignmentsList
          misalignments={filteredMisalignments}
          goToDate={goToDate}
          selectedOrder={selectedOrder}
        />
      )}
    </WarningsDetail>
  );
};
